// qs-brush-Square.ts
import type { KonvaLine } from '@/types/qs-type-konva'
import Konva from 'konva'
import { BaseBrush } from '../base/qs-brushes-base'
import type { BrushConfig, BrushContext, BrushDrawingResult, BrushProperties, BrushRecreationContext } from '../qs-brushes-types'

export class SquareBrush extends BaseBrush {
  readonly config: BrushConfig = {
    id: 'square',
    name: 'Square Brush',
    category: 'basic',
    description: 'Square brush with sharp edges',
    icon: '■',
    minSize: 1,
    maxSize: 100,
    defaultSize: 5
  }

  getProperties(size: number): BrushProperties {
    return {
      lineCap: 'square',
      lineJoin: 'miter',
      dashPattern: [],
      strokeWidth: size
    }
  }

  createBrushLine(context: BrushContext): KonvaLine {
    const properties = this.createBaseLineProperties(context)

    return new Konva.Line({
      id: `path-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      points: [context.position.x, context.position.y],
      ...properties,
      dash: [...properties.dashPattern]
    })
  }

  continueDrawing(existingLine: KonvaLine, context: BrushContext): void {
    const currentPoints = existingLine.points()
    const newPoints = currentPoints.concat([context.position.x, context.position.y])
    existingLine.points(newPoints)
  }

  async finishDrawing(existingLine: KonvaLine, context: BrushContext): Promise<BrushDrawingResult> {
    const points = existingLine.points()
    const properties = this.getProperties(context.size)
    const pathId = existingLine.id()

    const originalPath = this.createOriginalPath(points, context, pathId)

    return {
      konvaObjects: [existingLine],
      originalPath: originalPath,
      pathData: {
        id: pathId,
        layerId: context.layerId,
        points: points,
        stroke: context.color,
        strokeWidth: properties.strokeWidth,
        lineCap: properties.lineCap,
        lineJoin: properties.lineJoin,
        dash: properties.dashPattern || [],
        opacity: context.opacity,
        timestamp: Date.now(),
        brushType: this.config.id,
        isTapered: false,
        isThickToThin: false,
        isThinToThick: false,
        isSpray: false,
        isDiagonal: false,
        segmentCount: 0,
        dotCount: 0,
        visualData: {
          segments: [],
          dots: []
        }
      }
    }
  }

  async recreateFromData(context: BrushRecreationContext): Promise<void> {
    const { pathData, konvaLayer } = context
    const properties = this.getProperties(pathData.strokeWidth)
    const rawPoints = [...pathData.points]
    const smoothed = pathData.smoothedPoints && pathData.smoothedPoints.length >= 4
      ? [...pathData.smoothedPoints]
      : rawPoints

    const konvaLine = new Konva.Line({
      id: pathData.id,
      points: smoothed,
      stroke: pathData.stroke,
      strokeWidth: pathData.strokeWidth,
      lineCap: properties.lineCap,
      lineJoin: properties.lineJoin,
      dash: pathData.dash ? [...pathData.dash] : [...properties.dashPattern],
      opacity: pathData.opacity,
      draggable: false
    })

    konvaLayer.add(konvaLine)

    const originalPath = this.createOriginalPath(rawPoints, {
      position: { x: 0, y: 0 },
      size: pathData.strokeWidth,
      color: pathData.stroke,
      opacity: pathData.opacity,
      layerId: pathData.id
    }, pathData.id)

    konvaLayer.add(originalPath)
  }
}